/**
 * Gera o HTML completo para impressão de horários.
 * Extraído de HorarioTabCompleto.jsx para manter o componente mais limpo.
 */
import { getDaysInPeriod, isWeekend, isFeriado, calcularSemanas } from '../../utils/horarioUtils';
import { SECCOES } from '../../constants/horarioConstants';
import { calcularMetricasHorario } from '../../utils/calcularHorarios';
import { formatarHoras } from '../../utils/formatarHoras';

const PRINT_CSS = `
  @page { size: A4 landscape; margin: 10mm; }
  @media print { body { -webkit-print-color-adjust: exact; print-color-adjust: exact; } }
  body { font-family: Arial, sans-serif; font-size: 6pt; margin: 0; padding: 0; }
  .cabecalho { background-color: #f0f0f0; padding: 8px; margin-bottom: 8px; border: 1px solid #000; font-size: 6pt; line-height: 1.3; }
  .titulo { text-align: center; font-size: 10pt; font-weight: bold; margin-bottom: 8px; }
  table { width: 100%; border-collapse: collapse; margin-bottom: 10px; page-break-inside: avoid; }
  th, td { border: 1px solid #000; padding: 2px; text-align: center; font-size: 6pt; }
  th { background-color: #d0d0d0; font-weight: bold; }
  .colaborador-nome { text-align: left; font-weight: bold; background-color: #e8e8e8; min-width: 80px; }
  .weekend { background-color: #fff3cd; }
  .holiday { background-color: #ffcccc; }
  .turno { font-size: 5pt; line-height: 1.1; margin: 0; padding: 2px; margin-bottom: 1px; border-radius: 2px; }
  .turno-normal { background-color: transparent; border: 1px solid #d1d5db; }
  .turno-folga { background-color: #fde047; border: 2px solid #eab308; font-weight: bold; }
  .turno-ferias { background-color: #bbf7d0; border: 2px solid #22c55e; }
  .turno-falta { background-color: #fecaca; border: 2px solid #ef4444; }
  .turno-reuniao { background-color: #e9d5ff; border: 2px solid #a855f7; }
  .turno-troca { background-color: #fbcfe8; border: 2px solid #ec4899; }
  .turno-gestao { background-color: #fed7aa; border: 2px solid #f97316; }
  .turno-apoio { background-color: #c7d2fe; border: 2px solid #6366f1; }
  .turno-baixa { background-color: #fef08a; border: 2px solid #eab308; }
  /* FIX 28-06-2026: adicionar CSS para tipos que faltavam na impressão */
  .turno-integracao { background-color: #99f6e4; border: 2px solid #0d9488; }
  .turno-formacao { background-color: #fce7f3; border: 2px solid #db2777; }
  .secao-cafe { background-color: #fed7aa; color: #7c2d12; font-weight: bold; padding: 4px; border-radius: 3px; }
  .secao-avelal { background-color: #bfdbfe; color: #1e3a8a; font-weight: bold; padding: 4px; border-radius: 3px; }
  .secao-satao { background-color: #bbf7d0; color: #14532d; font-weight: bold; padding: 4px; border-radius: 3px; }
  .seccao-titulo { background-color: #333; color: white; padding: 4px; font-weight: bold; font-size: 8pt; margin-top: 8px; }
  .total-col { background-color: #d4edda; font-weight: bold; }
`;

function getSeccaoClass(local) {
  if (local === 'Cafe' || local === 'Café') return 'secao-cafe';
  if (local === 'Loja Avelal') return 'secao-avelal';
  if (local === 'Loja Sátão' || local === 'Loja Satao') return 'secao-satao';
  return '';
}

function renderTurno(turno) {
  const classeSeccao = getSeccaoClass(turno.local);
  const badgeTroca = turno.e_troca
    ? '<span style="background: linear-gradient(to right, #f97316, #ef4444); color: white; font-size: 5pt; font-weight: bold; padding: 1px 4px; border-radius: 8px; margin-left: 2px;">TROCA</span>'
    : '';

  // Helper para renderizar um turno com horas (integracao, apoio, formacao, reuniao com detalhes)
  // 🐛 FIX 28-06-2026: antes destes cases mostravam só o label sem horário,
  // e `integracao`/`formacao` não estavam sequer no switch → apareciam vazios
  // na impressão. Reportado pelo utilizador ("integrações, apoios não
  // aparecem"). Agora todos incluem local + entrada + saída + horas + label.
  const renderComHorario = (labelClasse, label) => `<div class="turno ${labelClasse}">
    <strong>${turno.local || ''}</strong>${badgeTroca}<br>
    ${turno.entrada || ''}<br>${turno.saida || ''}<br>
    <span style="font-size:5.5pt;font-weight:bold;">${label}</span>
    ${turno.horas ? `<br><strong>${formatarHoras(parseFloat(turno.horas).toFixed(2))}</strong>` : ''}
  </div>`;

  switch (turno.tipo) {
    case 'normal':
      return `<div class="turno ${classeSeccao}">
        <strong>${turno.local || ''}</strong>${badgeTroca}<br>
        ${turno.entrada || ''}<br>${turno.saida || ''}<br>
        <strong>${turno.horas ? formatarHoras(parseFloat(turno.horas).toFixed(2)) : ''}</strong>
      </div>`;
    case 'folga':      return '<div class="turno turno-folga">FOLGA</div>';
    case 'folga_compensacao': return '<div class="turno turno-folga">FOLGA C.</div>';
    case 'ferias':     return '<div class="turno turno-ferias">Ferias</div>';
    case 'falta':      return '<div class="turno turno-falta">Falta</div>';
    case 'gestao':     return '<div class="turno turno-gestao">Gestao</div>';
    case 'baixa':      return '<div class="turno turno-baixa">Baixa</div>';
    case 'troca':
      return `<div class="turno ${classeSeccao}"><strong>${turno.local || ''}</strong><br>Troca</div>`;
    // Tipos que TÊM horário — mostrar detalhes completos + label
    case 'integracao': return renderComHorario('turno-integracao', 'INTEGRAÇÃO');
    case 'apoio':      return renderComHorario('turno-apoio', 'APOIO');
    case 'formacao':   return renderComHorario('turno-formacao', 'FORMAÇÃO');
    case 'reuniao':    return renderComHorario('turno-reuniao', 'REUNIÃO');
    default:
      // Fallback: se aparecer um tipo novo desconhecido, pelo menos mostrar
      // o que existe (não deixar célula vazia).
      if (turno.local || turno.entrada || turno.horas) {
        return `<div class="turno ${classeSeccao}">
          <strong>${turno.local || ''}</strong>${badgeTroca}<br>
          ${turno.entrada || ''}<br>${turno.saida || ''}<br>
          <span style="font-size:5.5pt;">${(turno.tipo || '').toUpperCase()}</span>
          ${turno.horas ? `<br><strong>${formatarHoras(parseFloat(turno.horas).toFixed(2))}</strong>` : ''}
        </div>`;
      }
      return '';
  }
}

/**
 * Gera HTML completo para impressão do horário.
 * @param {Object} params
 * @param {string} params.inicio - Data início do período (YYYY-MM-DD)
 * @param {string} params.fim - Data fim do período (YYYY-MM-DD)
 * @param {Array} params.colaboradores - Lista de colaboradores com registos
 * @param {string} params.cabecalho - HTML do cabeçalho da empresa
 * @returns {string} HTML completo pronto para impressão
 */
export function generatePrintHTML({ inicio, fim, colaboradores, cabecalho }) {
  const days = getDaysInPeriod(inicio, fim);
  const colaboradoresPorSeccao = SECCOES.map(seccao => ({
    seccao,
    colaboradores: colaboradores.filter(c => c.seccao === seccao && c.mostrar_em_horario !== false)
  }));

  let html = `<!DOCTYPE html><html><head><meta charset="utf-8">
    <title>Horario - ${inicio} a ${fim}</title><style>${PRINT_CSS}</style></head><body>
    <div class="cabecalho">${cabecalho}</div>
    <div class="titulo">HORARIO DE TRABALHO - ${inicio} a ${fim}</div>`;

  colaboradoresPorSeccao.forEach(({ colaboradores: colabsSeccao }) => {
    if (colabsSeccao.length === 0) return;

    html += '<table><thead><tr><th class="colaborador-nome">Colaborador</th>';
    days.forEach(day => {
      const cls = isFeriado(day) ? 'holiday' : isWeekend(day) ? 'weekend' : '';
      const wd = new Date(day + 'T00:00:00').toLocaleDateString('pt-PT', { weekday: 'short' });
      const dm = new Date(day + 'T00:00:00').toLocaleDateString('pt-PT', { day: '2-digit', month: '2-digit' });
      html += `<th class="${cls}">${wd}<br>${dm}</th>`;
    });
    html += '<th class="total-col">Total H</th><th class="total-col">Ferias</th><th class="total-col">Faltas</th>';
    html += '<th class="total-col">Baixas</th><th class="total-col">Gestao</th><th class="total-col">Apoio</th>';
    html += '<th class="total-col">Folgas</th><th class="total-col">Banco Ant.</th><th class="total-col">Banco H</th>';
    html += '</tr></thead><tbody>';

    colabsSeccao.forEach(colab => {
      const semanasCalc = calcularSemanas(inicio, fim);
      const anoRef = new Date(inicio + 'T00:00:00').getFullYear();
      const metricas = calcularMetricasHorario(colab, semanasCalc, null, anoRef);

      // Heurística: detectar se o colaborador tem actividade no período (algum
      // turno normal). Se sim, dias com array vazio são considerados FOLGA.
      // Caso contrário (colaborador inactivo/sem horário), deixa célula vazia.
      const temActividade = Object.values(colab.registos_por_dia || {}).some(
        ts => Array.isArray(ts) && ts.some(t => t && t.tipo === 'normal')
      );

      html += `<tr><td class="colaborador-nome">${colab.colaborador_nome}</td>`;

      days.forEach(day => {
        const cls = isFeriado(day) ? 'holiday' : isWeekend(day) ? 'weekend' : '';
        const turnos = colab.registos_por_dia?.[day] || [];
        let cellHtml;
        if (turnos.length === 0) {
          // Sem turnos guardados: assumir FOLGA quando o colaborador tem
          // actividade no período (mês ativo). Caso contrário, vazio.
          cellHtml = temActividade
            ? '<div class="turno turno-folga">FOLGA</div>'
            : '';
        } else {
          cellHtml = turnos.map(renderTurno).join('');
        }
        html += `<td class="${cls}">${cellHtml}</td>`;
      });

      const { totalHoras, totalHorasNormais, totalHorasFeriado, totalHorasFerias, totalHorasReunioes, totalHorasGestao, totalHorasBaixa,
        diasFerias, diasFalta, diasFolga, diasFolgaComp, diasBaixa, diasGestao, turnosApoio, bancoAnterior, bancoTotal: bancoHoras } = metricas;

      const breakdown = [`Normal: ${formatarHoras(totalHorasNormais.toFixed(2))}`];
      if (totalHorasFeriado > 0) breakdown.push(`Feriado: ${formatarHoras(totalHorasFeriado.toFixed(2))}`);
      if (diasFerias > 0) breakdown.push(`Ferias: ${diasFerias}d (${formatarHoras(totalHorasFerias.toFixed(2))})`);
      if (totalHorasReunioes > 0) breakdown.push(`Reunioes: ${formatarHoras(totalHorasReunioes.toFixed(2))}`);
      if (diasGestao > 0) breakdown.push(`Gestao: ${diasGestao}d (${formatarHoras(totalHorasGestao.toFixed(2))})`);
      if (diasBaixa > 0) breakdown.push(`Baixa: ${diasBaixa}d (${formatarHoras(totalHorasBaixa.toFixed(2))})`);
      if (diasFolgaComp > 0) breakdown.push(`Folga Comp.: ${diasFolgaComp}d (${formatarHoras((diasFolgaComp * 8).toFixed(2))})`);

      html += `<td class="total-col" style="text-align: left; font-size: 5pt;">
        <div style="font-weight: bold; font-size: 6pt;">${formatarHoras(totalHoras.toFixed(2))}</div>
        <div style="font-size: 5pt; color: #666;">${breakdown.join('<br>')}</div></td>`;
      html += `<td class="total-col">${diasFerias}</td>`;
      html += `<td class="total-col">${diasFalta}</td>`;
      html += `<td class="total-col">${diasBaixa}</td>`;
      html += `<td class="total-col">${diasGestao}</td>`;
      html += `<td class="total-col">${turnosApoio}</td>`;
      html += `<td class="total-col">${diasFolga}${diasFolgaComp > 0 ? ` (${diasFolgaComp}FC)` : ''}</td>`;
      html += `<td class="total-col">${formatarHoras(bancoAnterior)}</td>`;
      html += `<td class="total-col" style="color: ${bancoHoras >= 0 ? 'green' : 'red'}; font-weight: bold;">${formatarHoras(bancoHoras)}</td>`;
      html += '</tr>';
    });

    html += '</tbody></table>';
  });

  html += '</body></html>';
  return html;
}

/**
 * Abre uma nova janela de impressão com o HTML gerado.
 * @param {string} printHTML - HTML completo para impressão
 * @param {boolean} autoPrint - Se deve abrir o diálogo de impressão automaticamente
 */
export function openPrintWindow(printHTML, autoPrint = true) {
  const printWindow = window.open('', '_blank');
  printWindow.document.write(printHTML);
  printWindow.document.close();
  if (autoPrint) {
    setTimeout(() => { printWindow.print(); }, 500);
  }
}
